/**
 * TokenManager - Secure token storage and management
 * Handles access tokens, refresh tokens, and token expiry
 */

interface TokenData {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

class TokenManager {
  private readonly ACCESS_TOKEN_KEY = 'huslrz_access_token';
  private readonly REFRESH_TOKEN_KEY = 'huslrz_refresh_token';
  private readonly TOKEN_EXPIRY_KEY = 'huslrz_token_expiry';
  private readonly TOKEN_TYPE_KEY = 'huslrz_token_type';
  private refreshTokenTimeout: NodeJS.Timeout | null = null;

  /**
   * Store tokens securely
   * Access token: sessionStorage (cleared on browser close)
   * Refresh token: localStorage (persists across sessions)
   */
  setTokens(data: TokenData): void {
    try {
      // Store access token in sessionStorage (cleared on browser close)
      sessionStorage.setItem(this.ACCESS_TOKEN_KEY, data.accessToken);
      
      // Store refresh token in localStorage (persists)
      localStorage.setItem(this.REFRESH_TOKEN_KEY, data.refreshToken);
      
      // Calculate and store expiry time
      const expiryTime = Date.now() + (data.expiresIn * 1000);
      sessionStorage.setItem(this.TOKEN_EXPIRY_KEY, expiryTime.toString());
      
      // Store token type
      sessionStorage.setItem(this.TOKEN_TYPE_KEY, data.tokenType || 'Bearer');
      
      // Schedule token refresh before expiry (5 minutes before)
      this.scheduleTokenRefresh(data.expiresIn);
    } catch (error) {
      console.error('Error storing tokens:', error);
    }
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    try {
      const token = sessionStorage.getItem(this.ACCESS_TOKEN_KEY);
      
      // Check if token is expired
      if (token && this.isTokenExpired()) {
        this.clearAccessToken();
        return null;
      }
      
      return token;
    } catch (error) {
      console.error('Error retrieving access token:', error);
      return null;
    }
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    try {
      return localStorage.getItem(this.REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error('Error retrieving refresh token:', error);
      return null;
    }
  }

  /**
   * Get token type (usually 'Bearer')
   */
  getTokenType(): string {
    try {
      return sessionStorage.getItem(this.TOKEN_TYPE_KEY) || 'Bearer';
    } catch (error) {
      return 'Bearer';
    }
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    try {
      const expiryTime = sessionStorage.getItem(this.TOKEN_EXPIRY_KEY);
      if (!expiryTime) return true;
      
      return Date.now() > parseInt(expiryTime, 10);
    } catch (error) {
      return true;
    }
  }

  /**
   * Get time until token expiry (in seconds)
   */
  getTimeUntilExpiry(): number {
    try {
      const expiryTime = sessionStorage.getItem(this.TOKEN_EXPIRY_KEY);
      if (!expiryTime) return 0;
      
      const timeLeft = parseInt(expiryTime, 10) - Date.now();
      return Math.max(0, Math.floor(timeLeft / 1000));
    } catch (error) {
      return 0;
    }
  }

  /**
   * Clear access token (on logout or expiry)
   */
  clearAccessToken(): void {
    try {
      sessionStorage.removeItem(this.ACCESS_TOKEN_KEY);
      sessionStorage.removeItem(this.TOKEN_EXPIRY_KEY);
      sessionStorage.removeItem(this.TOKEN_TYPE_KEY);
      this.cancelTokenRefresh();
    } catch (error) {
      console.error('Error clearing access token:', error);
    }
  }

  /**
   * Clear all tokens (on logout)
   */
  clearAllTokens(): void {
    try {
      sessionStorage.removeItem(this.ACCESS_TOKEN_KEY);
      sessionStorage.removeItem(this.TOKEN_EXPIRY_KEY);
      sessionStorage.removeItem(this.TOKEN_TYPE_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      this.cancelTokenRefresh();
    } catch (error) {
      console.error('Error clearing all tokens:', error);
    }
  }

  /**
   * Schedule token refresh before expiry
   * Refresh 5 minutes before actual expiry
   */
  private scheduleTokenRefresh(expiresIn: number): void {
    this.cancelTokenRefresh();
    
    // Refresh 5 minutes (300 seconds) before expiry
    const refreshTime = Math.max(0, (expiresIn - 300) * 1000);
    
    this.refreshTokenTimeout = setTimeout(() => {
      // Dispatch refresh token event
      window.dispatchEvent(new CustomEvent('tokenRefreshNeeded'));
    }, refreshTime);
  }

  /**
   * Cancel scheduled token refresh
   */
  private cancelTokenRefresh(): void {
    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
      this.refreshTokenTimeout = null;
    }
  }

  /**
   * Check if user has valid token
   */
  hasValidToken(): boolean {
    const token = this.getAccessToken();
    return !!token && !this.isTokenExpired();
  }

  /**
   * Get authorization header
   */
  getAuthorizationHeader(): string {
    const token = this.getAccessToken();
    const tokenType = this.getTokenType();
    
    if (!token) return '';
    return `${tokenType} ${token}`;
  }
}

const tokenManager = new TokenManager();
export default tokenManager;
